import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Check, X, Star, ExternalLink, BookmarkPlus, Search, Plus, Trash2, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { publicApi } from '../utils/api';
import { mergeGuestStackTools, parseGuestStack, saveGuestStack } from '../utils/runningStackStore';
import { useTheme } from '../contexts/ThemeContext';
import { IMG } from '../lib/images';
import { buildToolGoUrl } from '../utils/affiliateLinks';

// Pre-seeded popular comparisons for quick start
const QUICK_COMPARE = [
  { label: 'ChatGPT vs Claude', ids: ['chatgpt', 'claude'], emoji: '✏️' },
  { label: 'Midjourney vs DALL-E', ids: ['midjourney', 'dalle-3'], emoji: '🎨' },
  { label: 'n8n vs Make', ids: ['n8n', 'make'], emoji: '⚡' },
  { label: 'Jasper vs Notion AI', ids: ['jasper', 'notion-ai'], emoji: '💡' },
];

const ToolPickerEmpty = ({ allTools, loading, isLight }) => {
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [pricingFilter, setPricingFilter] = useState('all');
  const [ratingFilter, setRatingFilter] = useState('all');
  const navigate = useNavigate();

  const categories = useMemo(() => {
    const uniq = new Set();
    allTools.forEach((tool) => {
      if (tool.category_name) uniq.add(tool.category_name);
    });
    return Array.from(uniq).sort((a, b) => a.localeCompare(b));
  }, [allTools]);

  const parsePriceValue = (tool) => {
    const price = tool?.pricing_tiers?.[0]?.price;
    if (!price) return 0;
    if (/free/i.test(price)) return 0;
    const numeric = Number(String(price).replace(/[^0-9.]/g, ''));
    return Number.isFinite(numeric) ? numeric : 0;
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return allTools.filter((t) => {
      const matchesSearch = !q || t.name?.toLowerCase().includes(q) || t.category_name?.toLowerCase().includes(q);
      const matchesCategory = categoryFilter === 'all' || t.category_name === categoryFilter;
      const priceValue = parsePriceValue(t);
      const matchesPricing =
        pricingFilter === 'all' ||
        (pricingFilter === 'free' && priceValue === 0) ||
        (pricingFilter === 'paid' && priceValue > 0) ||
        (pricingFilter === 'under50' && priceValue > 0 && priceValue < 50) ||
        (pricingFilter === '50plus' && priceValue >= 50);
      const overall = Number(t?.rating?.overall || 0);
      const matchesRating =
        ratingFilter === 'all' ||
        (ratingFilter === '4plus' && overall >= 4) ||
        (ratingFilter === '45plus' && overall >= 4.5);
      return matchesSearch && matchesCategory && matchesPricing && matchesRating;
    });
  }, [allTools, search, categoryFilter, pricingFilter, ratingFilter]);

  const toggle = (tool) => {
    if (selected.find((t) => t.id === tool.id)) {
      setSelected(selected.filter((t) => t.id !== tool.id));
    } else if (selected.length < 3) {
      setSelected([...selected, tool]);
    }
  };

  const handleCompare = () => {
    if (selected.length >= 2) {
      navigate(`/compare?tools=${selected.map((t) => t.id).join(',')}`);
    }
  };

  const handleQuick = (slugs) => {
    const matched = allTools.filter((t) => slugs.includes(t.slug)).slice(0, 3);
    if (matched.length >= 2) navigate(`/compare?tools=${matched.map((t) => t.id).join(',')}`);
  };

  return (
    <div
      className="min-h-screen pt-24"
      style={{
        backgroundImage: isLight
          ? `linear-gradient(135deg, rgba(255,252,241,0.94), rgba(247,252,255,0.90)), url(${IMG.hero.compare})`
          : `linear-gradient(rgba(8,8,18,0.84), rgba(8,8,18,0.96)), url(${IMG.hero.compare})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}
      data-testid="comparison-page"
    >
      <Helmet>
        <title>Compare AI Tools Side-by-Side | Wowrolla</title>
        <meta name="description" content="Compare any 2-3 AI tools side-by-side by persona fit, implementation complexity, pricing, and ROI signals." />
      </Helmet>
      <div className="container mx-auto px-6 pb-24">
        <Link to="/" className="inline-flex items-center mb-8 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <ArrowLeft size={15} className="mr-2" /> Back to Home
        </Link>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          <p className="text-xs font-mono uppercase tracking-[0.2em] mb-3" style={{ color: 'var(--accent-primary)' }}>Head-to-Head Analysis</p>
          <h1 className="text-4xl sm:text-5xl font-black mb-4" style={{ fontFamily: 'Unbounded, sans-serif', color: isLight ? 'var(--text-primary)' : '#ffffff' }}>Compare AI Tools</h1>
          <p className="text-lg mb-8 max-w-2xl" style={{ color: 'var(--text-secondary)' }}>
            Pick 2 - 3 tools below for a side-by-side breakdown: persona fit, pricing, implementation complexity, pros/cons, and an AI verdict.
          </p>
        </motion.div>

        {/* Quick compare shortcuts */}
        <div className="mb-8">
          <p className="text-xs font-mono uppercase tracking-[0.16em] mb-3" style={{ color: 'var(--text-muted)' }}>Popular Comparisons</p>
          <div className="flex flex-wrap gap-2">
            {QUICK_COMPARE.map((qc) => (
              <button key={qc.label} onClick={() => loading ? null : handleQuick(qc.ids)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all hover:scale-105"
                style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-strong)', color: 'var(--text-secondary)' }}
                disabled={loading}
              >
                <span>{qc.emoji}</span> {qc.label} <ArrowRight size={12} />
              </button>
            ))}
          </div>
        </div>

        {/* Main picker */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Tool search panel */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold" style={{ color: 'var(--text-primary)' }}>Select Tools</h2>
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Up to 3 tools</span>
            </div>
            {/* Search */}
            <div className="relative mb-4">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by tool name or category..."
                className="w-full pl-10 pr-4 py-3 rounded-xl text-sm"
                style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-strong)', color: 'var(--text-primary)' }}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }}
              >
                <option value="all">All categories</option>
                {categories.map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
              <select
                value={pricingFilter}
                onChange={(e) => setPricingFilter(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }}
              >
                <option value="all">All pricing</option>
                <option value="free">Free</option>
                <option value="paid">Paid</option>
                <option value="under50">Under $50/mo</option>
                <option value="50plus">$50+/mo</option>
              </select>
              <select
                value={ratingFilter}
                onChange={(e) => setRatingFilter(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }}
              >
                <option value="all">All ratings</option>
                <option value="4plus">Rating 4.0+</option>
                <option value="45plus">Rating 4.5+</option>
              </select>
            </div>
            <div className="flex items-center justify-between mb-3 text-xs" style={{ color: 'var(--text-muted)' }}>
              <span>{filtered.length} tools available</span>
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setCategoryFilter('all');
                  setPricingFilter('all');
                  setRatingFilter('all');
                }}
                className="hover:underline"
                style={{ color: 'var(--accent-primary)' }}
              >
                Reset filters
              </button>
            </div>
            {loading ? (
              <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyber-yellow" /></div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[62vh] overflow-y-auto pr-1">
                {filtered.map((tool) => {
                  const isSelected = !!selected.find((t) => t.id === tool.id);
                  const isDisabled = !isSelected && selected.length >= 3;
                  return (
                    <button key={tool.id} onClick={() => toggle(tool)} disabled={isDisabled}
                      className={`text-left p-3 rounded-xl transition-all ${isDisabled ? 'opacity-40 cursor-not-allowed' : 'hover:scale-[1.02]'}`}
                      style={{
                        background: isSelected ? 'rgba(0,255,135,0.08)' : 'var(--bg-elevated)',
                        border: isSelected ? '1.5px solid rgba(0,255,135,0.5)' : '1px solid var(--border-subtle)',
                      }}
                    >
                      <div className="flex items-start justify-between gap-1 mb-1">
                        <span className="font-semibold text-sm truncate" style={{ color: 'var(--text-primary)' }}>{tool.name}</span>
                        <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: isSelected ? '#00ff87' : 'var(--bg-base)', border: isSelected ? 'none' : '1px solid var(--border-strong)' }}>
                          {isSelected ? <Check size={11} color="#000" /> : <Plus size={11} style={{ color: 'var(--text-muted)' }} />}
                        </div>
                      </div>
                      <div className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>{tool.category_name || 'AI Tool'}</div>
                      {tool.rating?.overall && (
                        <div className="flex items-center gap-1 mt-1">
                          <Star size={10} fill="#FFE400" style={{ color: '#FFE400' }} />
                          <span className="text-xs" style={{ color: '#FFE400' }}>{tool.rating.overall}</span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Selection summary panel */}
          <div className="lg:col-span-1">
            <div className="sticky top-28 p-5 rounded-2xl" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
              <h3 className="font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Your Comparison</h3>
              {selected.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-3xl mb-3">⚡</div>
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Select 2 or 3 tools to compare</p>
                </div>
              ) : (
                <div className="space-y-2 mb-5">
                  {selected.map((t) => (
                    <div key={t.id} className="flex items-center justify-between p-2.5 rounded-lg" style={{ background: 'var(--bg-base)' }}>
                      <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{t.name}</span>
                      <button onClick={() => toggle(t)} className="p-1 rounded-md hover:opacity-70" style={{ color: 'var(--text-muted)' }}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                  {[...Array(3 - selected.length)].map((_, i) => (
                    <div key={i} className="p-2.5 rounded-lg border border-dashed" style={{ borderColor: 'var(--border-subtle)' }}>
                      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>+ Add another tool</span>
                    </div>
                  ))}
                </div>
              )}
              <motion.button
                onClick={handleCompare}
                disabled={selected.length < 2}
                whileHover={selected.length >= 2 ? { scale: 1.03 } : {}}
                className="w-full py-3 rounded-xl font-bold text-sm transition-all"
                style={{
                  background: selected.length >= 2 ? 'var(--accent-primary)' : 'var(--bg-base)',
                  color: selected.length >= 2 ? '#000' : 'var(--text-muted)',
                  cursor: selected.length >= 2 ? 'pointer' : 'not-allowed',
                  border: selected.length >= 2 ? 'none' : '1px solid var(--border-subtle)',
                }}
              >
                {selected.length < 2 ? `Select ${2 - selected.length} more` : `Compare ${selected.length} Tools ->`}
              </motion.button>
              <Link to="/find" className="block text-center text-xs mt-4" style={{ color: 'var(--text-muted)' }}>
                Not sure which tools to compare? <span style={{ color: 'var(--accent-primary)' }}>Try the Finder -></span>
              </Link>
              {selected.length >= 2 && (
                <Link
                  to={`/find?from=compare&tools=${selected.map((t) => t.slug).join(',')}`}
                  className="block text-center text-xs mt-2"
                  style={{ color: '#39FF14' }}
                  data-testid="compare-refine-with-finder"
                >
                  Refine this shortlist with AI advisor ->
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Feature highlights */}
        <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { icon: '†', title: 'Persona Fit Scores', sub: 'Solo / SMB / Enterprise' },
            { icon: '’°', title: 'Pricing Breakdown', sub: 'All tiers compared' },
            { icon: '⚡', title: 'Implementation Complexity', sub: 'Time-to-value signals' },
            { icon: '¤–', title: 'AI Verdict', sub: 'Context-aware recommendation' },
          ].map((f) => (
            <div key={f.title} className="p-4 rounded-xl text-center" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
              <div className="text-2xl mb-2">{f.icon}</div>
              <div className="font-semibold text-sm mb-1" style={{ color: 'var(--text-primary)' }}>{f.title}</div>
              <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{f.sub}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};


const ComparisonPage = () => {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const [searchParams] = useSearchParams();
  const [tools, setTools] = useState([]);
  const [allTools, setAllTools] = useState([]);
  const [comparisonPayload, setComparisonPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePersona, setActivePersona] = useState('solo');
  const [showRationales, setShowRationales] = useState(false);

  const personaLabels = {
    solo: 'Solo',
    smb: 'SMB',
    enterprise: 'Enterprise',
  };

  useEffect(() => {
    const loadComparison = async () => {
      try {
        // Always load all tools for the picker and enrich with category names.
        const [allToolsResult, categoriesResult] = await Promise.allSettled([
          publicApi.getAllTools(false),
          publicApi.getCategories(),
        ]);
        const allToolsData = allToolsResult.status === 'fulfilled' ? (allToolsResult.value.data || []) : [];
        const categoryData = categoriesResult.status === 'fulfilled' ? (categoriesResult.value.data || []) : [];

        const categoryById = Object.fromEntries(categoryData.map((cat) => [cat.id, cat.name]));
        const normalizedAllTools = allToolsData.map((tool) => ({
          ...tool,
          category_name: tool.category_name || categoryById[tool.category_id] || 'AI Tool',
        }));
        setAllTools(normalizedAllTools);

        const toolIds = searchParams.get('tools')?.split(',').filter(Boolean) || [];
        if (toolIds.length === 0) {
          setLoading(false);
          return;
        }
        const response = await publicApi.compareTools(toolIds);
        setTools(response.data.tools);
        setComparisonPayload(response.data);
      } catch (error) {
        console.error('Failed to load comparison:', error);
      } finally {
        setLoading(false);
      }
    };
    loadComparison();
  }, [searchParams]);

  const saveComparisonToGuestStack = () => {
    const items = tools.map((tool) => ({
      tool_id: tool.id,
      slug: tool.slug,
      name: tool.name,
      notes: 'Saved from comparison',
      added_date: new Date().toISOString(),
      monthly_cost: tool.pricing_tiers?.[0]?.price || '0',
    }));

    try {
      const current = parseGuestStack();
      saveGuestStack(mergeGuestStackTools(current, items));
    } catch (error) {
      console.warn('Failed to save comparison to local stack', error);
    }
  };

  const personaBestToolId = comparisonPayload?.persona_scores?.[activePersona]?.[0]?.tool_id;

  const dimensionsForView = useMemo(() => comparisonPayload?.dimensions || [], [comparisonPayload]);

  const faq = [
    {
      q: 'Does Wowrolla rank by commission?',
      a: 'No. Comparison verdicts are driven by fit, implementation friction, and value signals. Commission is disclosed separately for transparency.',
    },
    {
      q: 'How should I choose between similar scores?',
      a: 'Use your workflow constraints and rollout readiness as tie-breakers. If two tools score similarly, implementation complexity often decides outcome quality.',
    },
  ];

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faq.map((entry) => ({
      '@type': 'Question',
      name: entry.q,
      acceptedAnswer: { '@type': 'Answer', text: entry.a },
    })),
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="text-center text-text-secondary">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-cyber-yellow"></div>
          <p className="mt-4">Loading comparison...</p>
        </div>
      </div>
    );
  }

  if (tools.length === 0) {
    return <ToolPickerEmpty allTools={allTools} loading={loading} isLight={isLight} />;
  }

  return (
    <div className="min-h-screen pt-24" style={{ background: 'var(--bg-base)' }} data-testid="comparison-page">
      <Helmet>
        <title>AI Tool Comparison | Wowrolla</title>
        <meta name="description" content="Compare AI tools by persona fit, implementation complexity, and practical buying confidence." />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <section
        className="relative overflow-hidden"
        style={{
          backgroundImage: isLight
            ? `linear-gradient(135deg, rgba(255,252,241,0.92), rgba(247,252,255,0.88)), url(${IMG.hero.compare})`
            : `linear-gradient(rgba(8,8,18,0.76), rgba(8,8,18,0.96)), url(${IMG.hero.compare})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 prime-aurora-bg" style={{ opacity: isLight ? 0.22 : 1 }} />
        <div className="container mx-auto px-6 py-14 relative z-10">
        <Link 
          to="/" 
          className="inline-flex items-center text-text-secondary hover:text-cyber-yellow transition-colors mb-8"
          data-testid="back-link"
        >
          <ArrowLeft size={16} className="mr-2" />
          Back to Home
        </Link>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 
            className="text-4xl sm:text-5xl font-black mb-4"
            style={{ fontFamily: 'Unbounded, sans-serif', color: isLight ? 'var(--text-primary)' : '#ffffff' }}
            data-testid="comparison-title"
          >
            Tool Comparison
          </h1>
          <p className="text-lg text-text-secondary mb-8">
            Compare {tools.length} AI tools side-by-side
          </p>
          <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="comparison-persona-tabs">
            {Object.entries(personaLabels).map(([persona, label]) => (
              <button
                key={persona}
                type="button"
                onClick={() => setActivePersona(persona)}
                className="px-4 py-2 text-sm font-semibold rounded-full border transition-colors"
                style={{
                  background: activePersona === persona ? '#FFE800' : 'transparent',
                  color: activePersona === persona ? '#000' : 'var(--text-secondary)',
                  borderColor: activePersona === persona ? '#FFE800' : 'var(--border-subtle)',
                }}
              >
                {label}
              </button>
            ))}
          </div>

          <div
            className="p-4 mb-8"
            style={{ borderLeft: '2px solid #FFE800', background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}
            data-testid="comparison-ai-verdict"
          >
            <p className="text-sm font-mono mb-2" style={{ color: 'var(--accent-primary)' }}>AI Verdict</p>
            <p className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>
              {comparisonPayload?.verdict?.summary || 'Best for you if you need balanced fit and practical execution speed.'}
            </p>
            {personaBestToolId && (
              <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                Best for {personaLabels[activePersona]} teams right now:{' '}
                <span style={{ color: 'var(--accent-primary)' }}>
                  {tools.find((tool) => tool.id === personaBestToolId)?.name || 'N/A'}
                </span>
                {' '} - {comparisonPayload?.persona_scores?.[activePersona]?.[0]?.verdict}
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-3 mb-6">
            <Link to="/dashboard/stack" onClick={saveComparisonToGuestStack} className="btn-primary inline-flex items-center gap-2">
              <BookmarkPlus size={16} /> Save this comparison
            </Link>
            <Link to="/my-stack/build" className="btn-secondary">Build a full stack from this</Link>
          </div>

          <Link to={`/find?from=compare&tools=${tools.map((tool) => tool.slug).join(',')}`} className="inline-flex items-center text-sm" style={{ color: 'var(--accent-primary)' }} data-testid="comparison-finder-link">
            Not ready to compare yet? Try the finder
          </Link>

          <div className="terminal-grid p-4 mt-6" data-testid="comparison-disclosure-confidence">
            <p className="text-xs uppercase tracking-[0.16em] font-mono text-cyber-yellow mb-2">Confidence disclosure</p>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              These rankings are designed to help you decide faster, not force a single winner. Always cross-check workflows and onboarding readiness.
              For higher-stakes purchases, use the consulting route for a tailored recommendation.
            </p>
            <Link to="/consulting" className="inline-flex mt-3 text-sm" style={{ color: 'var(--accent-primary)' }}>
              Talk to a consultant before committing
            </Link>
          </div>
        </motion.div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="container mx-auto px-6 pb-24">
        {dimensionsForView.length > 0 && (
          <div className="terminal-grid p-6 mb-6" data-testid="comparison-dimension-scoring">
            <div className="flex items-center justify-between mb-4 gap-3">
              <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: 'Unbounded, sans-serif' }}>
                Dimension Scoring (1-10)
              </h2>
              <button
                type="button"
                onClick={() => setShowRationales((prev) => !prev)}
                className="px-3 py-1 rounded-full text-xs font-semibold"
                style={{
                  border: '1px solid var(--border-subtle)',
                  color: 'var(--text-secondary)',
                  background: 'var(--bg-elevated)',
                }}
              >
                {showRationales ? 'Hide rationale' : 'Show rationale'}
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full" style={{ borderCollapse: 'collapse', minWidth: '680px' }}>
                <thead>
                  <tr className="border-b" style={{ borderColor: 'var(--border-subtle)' }}>
                    <th className="text-left py-2 pr-4 text-xs uppercase font-mono" style={{ color: 'var(--text-muted)' }}>Dimension</th>
                    {tools.map((tool) => (
                      <th key={`dim-head-${tool.id}`} className="text-left py-2 pr-4 text-xs uppercase font-mono" style={{ color: 'var(--text-muted)' }}>
                        {tool.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dimensionsForView.map((dimension) => (
                    <tr key={dimension} className="border-b" style={{ borderColor: 'var(--border-subtle)' }}>
                      <td className="py-3 pr-4 align-top">
                        <p className="text-xs font-semibold uppercase" style={{ color: 'var(--text-secondary)' }}>
                          {dimension.replaceAll('_', ' ')}
                        </p>
                      </td>
                      {tools.map((tool) => {
                        const scoreData = comparisonPayload?.dimension_scores?.[tool.id]?.[dimension] || { score: 0, rationale: 'No rationale available.' };
                        const score = Number(scoreData.score || 0);
                        const barColor = score >= 8 ? '#39FF14' : score >= 5 ? '#FFE800' : '#ff7d7d';
                        return (
                          <td key={`${tool.id}-${dimension}`} className="py-3 pr-4 align-top">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-mono px-1.5 py-0.5 rounded" style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border-subtle)' }}>
                                {score.toFixed(1)}
                              </span>
                              <div className="h-1.5 w-24" style={{ background: 'rgba(255,255,255,0.12)' }}>
                                <div style={{ width: `${Math.max(0, Math.min(100, score * 10))}%`, height: '100%', background: barColor }} />
                              </div>
                            </div>
                            {showRationales && (
                              <p className="text-[11px] leading-snug" style={{ color: 'var(--text-secondary)' }}>{scoreData.rationale}</p>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full terminal-grid" style={{ borderCollapse: 'collapse' }} data-testid="comparison-table">
            <thead>
              <tr className="border-b" style={{ borderColor: 'var(--border-subtle)' }}>
                <th className="p-4 text-left text-text-muted text-sm uppercase font-mono">Feature</th>
                {tools.map(tool => (
                  <th key={tool.id} className="p-4 text-left">
                    <div className="flex flex-col items-start">
                      {tool.logo_url && (
                        <img src={tool.logo_url} alt={tool.name} className="w-12 h-12 object-contain mb-2" />
                      )}
                      <div className="font-semibold text-lg" style={{ color: 'var(--text-primary)' }}>{tool.name}</div>
                      <div className="text-text-secondary text-sm">{tool.tagline}</div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Rating */}
              <tr className="border-b" style={{ borderColor: 'var(--border-subtle)' }}>
                <td className="p-4 text-text-secondary font-semibold">Overall Rating</td>
                {tools.map(tool => (
                  <td key={tool.id} className="p-4">
                    {tool.rating ? (
                      <div className="flex items-center gap-2">
                        <div className="flex items-center">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              size={16}
                              className={i < Math.floor(tool.rating.overall) ? 'text-cyber-yellow fill-cyber-yellow' : (isLight ? 'text-gray-400' : 'text-gray-600')}
                            />
                          ))}
                        </div>
                        <span className="font-mono" style={{ color: 'var(--text-primary)' }}>{tool.rating.overall.toFixed(1)}</span>
                      </div>
                    ) : (
                      <span className="text-text-muted">N/A</span>
                    )}
                  </td>
                ))}
              </tr>

              {/* Pricing */}
              <tr className="border-b" style={{ borderColor: 'var(--border-subtle)' }}>
                <td className="p-4 text-text-secondary font-semibold">Starting Price</td>
                {tools.map(tool => (
                  <td key={tool.id} className="p-4">
                    {tool.pricing_tiers && tool.pricing_tiers.length > 0 ? (
                      <div className="text-cyber-yellow font-semibold font-mono">{tool.pricing_tiers[0].price}</div>
                    ) : (
                      <span className="text-text-muted">N/A</span>
                    )}
                  </td>
                ))}
              </tr>

              {/* Best For */}
              <tr className="border-b" style={{ borderColor: 'var(--border-subtle)' }}>
                <td className="p-4 text-text-secondary font-semibold">Best For</td>
                {tools.map(tool => (
                  <td key={tool.id} className="p-4">
                    {tool.best_for && tool.best_for.length > 0 ? (
                      <ul className="space-y-1">
                        {tool.best_for.slice(0, 3).map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-text-secondary">
                            <Check size={14} className="text-laser-green mt-0.5 flex-shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span className="text-text-muted">N/A</span>
                    )}
                  </td>
                ))}
              </tr>

              {/* Pros */}
              <tr className="border-b" style={{ borderColor: 'var(--border-subtle)' }}>
                <td className="p-4 text-text-secondary font-semibold">Top Pros</td>
                {tools.map(tool => (
                  <td key={tool.id} className="p-4">
                    {tool.pros && tool.pros.length > 0 ? (
                      <ul className="space-y-1">
                        {tool.pros.slice(0, 3).map((pro, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-text-secondary">
                            <Check size={14} className="text-laser-green mt-0.5 flex-shrink-0" />
                            <span>{pro}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span className="text-text-muted">N/A</span>
                    )}
                  </td>
                ))}
              </tr>

              {/* Cons */}
              <tr className="border-b" style={{ borderColor: 'var(--border-subtle)' }}>
                <td className="p-4 text-text-secondary font-semibold">Top Cons</td>
                {tools.map(tool => (
                  <td key={tool.id} className="p-4">
                    {tool.cons && tool.cons.length > 0 ? (
                      <ul className="space-y-1">
                        {tool.cons.slice(0, 3).map((con, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-text-secondary">
                            <X size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
                            <span>{con}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span className="text-text-muted">N/A</span>
                    )}
                  </td>
                ))}
              </tr>

              {/* Actions */}
              <tr>
                <td className="p-4 text-text-secondary font-semibold">Actions</td>
                {tools.map(tool => (
                  <td key={tool.id} className="p-4">
                    <div className="flex flex-col gap-2">
                      <Link
                        to={`/tools/${tool.slug}`}
                        className="btn-secondary text-sm text-center"
                      >
                        View Details
                      </Link>
                      <a
                        href={buildToolGoUrl(tool)}
                        target="_blank"
                        rel="sponsored nofollow noopener noreferrer"
                        className="btn-primary text-sm inline-flex items-center justify-center gap-1"
                      >
                        Visit Site
                        <ExternalLink size={14} />
                      </a>
                    </div>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        <div className="terminal-grid p-6 mt-6" data-testid="comparison-faq-block">
          <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)', fontFamily: 'Unbounded, sans-serif' }}>FAQ</h2>
          <div className="space-y-3">
            {faq.map((entry) => (
              <div key={entry.q}>
                <h3 className="font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>{entry.q}</h3>
                <p className="text-text-secondary text-sm">{entry.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ComparisonPage;

